import { IVector2d } from '../types/math';
import { effectiveDistance, OVERLAPLENGTH, thresholdValue } from './const';
import { IPoint2d } from './IPoint2d';
import { Line2d } from './Line2d';
import { Point2dUtils } from './Point2dUtils';
import { Ray2d } from './ray2d';
import { normalizeVector, subtractParamIntervals } from './utils';
import * as THREE from 'three';

export class LineSeg2d {
  private _start: IPoint2d;
  private _end: IPoint2d;

  constructor(v0: IPoint2d, v1: IPoint2d) {
    this._start = v0;
    this._end = v1;
  }

  get start(): IPoint2d {
    return this._start;
  }
  set start(p: IPoint2d) {
    this._start = p;
  }

  get end(): IPoint2d {
    return this._end;
  }

  set end(p: IPoint2d) {
    this._end = p;
  }

  /** 线段长度 */
  get length() {
    return Point2dUtils.distanceTo(this._start, this._end);
  }

  get direction(): IVector2d {
    return Point2dUtils.getUnitDirection(this._start, this._end);
  }

  intersection(other: LineSeg2d): IPoint2d | null {
    // 实现线段相交算法
    // 这里使用向量叉积方法判断线段是否相交并计算交点
    // 简化实现，实际应用中可能需要更精确的计算
    const x1 = this.start.x,
      y1 = this.start.y;
    const x2 = this.end.x,
      y2 = this.end.y;
    const x3 = other.start.x,
      y3 = other.start.y;
    const x4 = other.end.x,
      y4 = other.end.y;

    const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
    // if (denom === 0) return null; // 平行或共线
    if (Math.abs(denom) < thresholdValue) return null; // 平行或共线

    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
    const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

    // 这里不能用0判断，要用误差值
    // if (ua < 0 || ua > 1 || ub < 0 || ub > 1) return null;
    if (ua < -thresholdValue || ua > 1 + thresholdValue || ub < -thresholdValue || ub > 1 + thresholdValue) {
      return null;
    }

    const intersectionPoint = { x: x1 + ua * (x2 - x1), y: y1 + ua * (y2 - y1) };
    if (Point2dUtils.equals(this.start, intersectionPoint)) {
      return { x: this.start.x, y: this.start.y };
    } else if (Point2dUtils.equals(this.end, intersectionPoint)) {
      return { x: this.end.x, y: this.end.y };
    } else if (Point2dUtils.equals(other.start, intersectionPoint)) {
      return { x: other.start.x, y: other.start.y };
    } else if (Point2dUtils.equals(other.end, intersectionPoint)) {
      return { x: other.end.x, y: other.end.y };
    }

    return { x: x1 + ua * (x2 - x1), y: y1 + ua * (y2 - y1) };
  }

  intersectionLine(other: Line2d): IPoint2d | null {
    const line = new Line2d({ points: { p1: this._start, p2: this._end } });
    const intersectPoint = line.intersection(other);
    if (intersectPoint && this.pointOnSegment(intersectPoint)) {
      return intersectPoint;
    }

    return null;
  }

  /**
   * 判断两条线段是否相交,不包括端点在线上的情况
   * @param other 另一条线段
   * @returns 相交点
   */
  crossPoint(other: LineSeg2d): IPoint2d | null {
    const intersectPoint = this.intersection(other);
    if (!intersectPoint) {
      return null;
    }

    if (
      Point2dUtils.equals(intersectPoint, this._start) ||
      Point2dUtils.equals(intersectPoint, this._end) ||
      Point2dUtils.equals(intersectPoint, other.start) ||
      Point2dUtils.equals(intersectPoint, other.end)
    ) {
      return null;
    }

    return intersectPoint;
  }

  // 辅助方法：判断点是否在线段上
  pointOnSegment(p: IPoint2d, tValue: number = thresholdValue): boolean {
    const cross =
      (p.x - this.start.x) * (this.end.y - this.start.y) - (p.y - this.start.y) * (this.end.x - this.start.x);
    // 这里判断点在线上精度不能太高
    // if (Math.abs(cross) > thresholdValue) return false;
    if (Math.abs(cross) > effectiveDistance) return false;

    const minX = Math.min(this.start.x, this.end.x);
    const maxX = Math.max(this.start.x, this.end.x);
    const minY = Math.min(this.start.y, this.end.y);
    const maxY = Math.max(this.start.y, this.end.y);

    // 2. 检查点 C 是否在 A 和 B 的范围内
    const isWithinX = Math.abs(minX - p.x) < tValue || Math.abs(maxX - p.x) < tValue || (p.x >= minX && p.x <= maxX);
    const isWithinY = Math.abs(minY - p.y) < tValue || Math.abs(maxY - p.y) < tValue || (p.y >= minY && p.y <= maxY);

    // return p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;

    return isWithinX && isWithinY;
  }

  /**
   * 判断线段是否在另一条线段上
   * @param seg 另一条线段
   * @returns 是否在另一条线段上
   */
  segmentOnSegment(seg: LineSeg2d): boolean {
    if (seg.pointOnSegment(this.start) && seg.pointOnSegment(this.end)) {
      return true;
    }

    return false;
  }

  isParallel(seg: LineSeg2d): boolean {
    // 计算两条线段的向量
    const vec1 = { x: this.end.x - this.start.x, y: this.end.y - this.start.y };
    const vec2 = { x: seg.end.x - seg.start.x, y: seg.end.y - seg.start.y };

    // 计算叉积
    const crossProduct = vec1.x * vec2.y - vec1.y * vec2.x;

    // 叉积接近0则认为平行（考虑浮点误差）
    return Math.abs(crossProduct) < thresholdValue;
  }

  isVertical(seg: LineSeg2d): boolean {
    const dx1 = seg.end.x - seg.start.x;
    const dy1 = seg.end.y - seg.start.y;
    const dx2 = this.end.x - this.start.x;
    const dy2 = this.end.y - this.start.y;

    // 检查叉积的绝对值是否等于向量长度的乘积
    const crossProduct = dx1 * dx2 + dy1 * dy2;
    return Math.abs(crossProduct) < thresholdValue;
  }

  /**
   * 判断点P相对于线段AB的位置
   * @param A 线段起点
   * @param B 线段终点
   * @param P 待判断的点
   * @returns
   *   `>0`: P在AB左侧
   *
   *   `<0`: P在AB右侧
   *
   *   `=0`: P在AB线上
   */
  pointLineSegSide(point: IPoint2d): number {
    const segVec = Point2dUtils.subtracted(Point2dUtils.clone(this._end), Point2dUtils.clone(this._start));
    const tempVec = Point2dUtils.subtracted(point, Point2dUtils.clone(this._start));

    // 计算二维叉积（忽略z轴）
    return segVec.x * tempVec.y - segVec.y * tempVec.x;
  }

  // 同pointLineSegSide
  pointSide(point: IPoint2d): number {
    return this.pointLineSegSide(point);
  }

  /**
   * 计算点沿着线段移动指定距离后的位置（可以超出端点）
   * @param distance 要移动的距离（正数向终点方向，负数向起点反方向）
   * @returns 移动后的点坐标
   */
  movePointAlongLine(point: IPoint2d, distance: number): IPoint2d {
    // 计算线段的方向向量
    const dx = this._end.x - this._start.x;
    const dy = this._end.y - this._start.y;

    // 计算线段长度
    const segmentLength = Math.sqrt(dx * dx + dy * dy);

    // 如果线段长度为0（起点和终点重合），直接返回起点
    if (segmentLength === 0) return point;

    // 计算单位方向向量
    const unitDx = dx / segmentLength;
    const unitDy = dy / segmentLength;

    // 计算移动后的点坐标
    return { x: point.x + unitDx * distance, y: point.y + unitDy * distance };
  }

  pointToSegmentDistance(point: IPoint2d): number {
    return pointToSegmentDistance(point.x, point.y, this.start.x, this.start.y, this.end.x, this.end.y);
  }

  pointToLineDistance(point: IPoint2d) {
    const line = new Line2d({ points: { p1: this._start, p2: this._end } });

    return line.pointToLineDistance(point);
  }

  getSegmentCenter(): IPoint2d {
    return { x: (this.start.x + this.end.x) / 2, y: (this.start.y + this.end.y) / 2 };
  }

  /**
   * 判断当前线段是否在另一条线段所在的直线上
   *
   * 注意：此方法不考虑线段是否有重叠，仅判断是否在同一直线上
   * @param other 另一条线段
   */
  isOnExtendedLineOf(other: LineSeg2d): boolean {
    // 是否平行
    if (!this.isParallel(other)) {
      return false;
    }

    // 平行前提下，只要起点在直线上，终点必然在直线上
    return Math.abs(other.pointLineSegSide(this.start)) < thresholdValue;
  }

  /**
   * 计算点在线段上的垂足比例
   * @param point 待计算的点
   * @returns 垂足比例
   */
  getFootRatio(point: IPoint2d): number {
    const x = point.x,
      y = point.y;
    const x1 = this.start.x,
      y1 = this.start.y;
    const x2 = this.end.x,
      y2 = this.end.y;

    const dx = x2 - x1;
    const dy = y2 - y1;

    // 线段长度为0，垂足就是起点
    if (dx === 0 && dy === 0) return 0;

    const t = ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy);

    return t;
  }

  /**
   * 计算点在直线上的垂足, 超出线段时返回线段端点
   * @param point 待计算的点
   * @param limit 是否限制垂足点在线段上
   * @returns 垂足点
   */
  getFootOfPerpendicular(point: IPoint2d, limit = true): IPoint2d {
    const t = this.getFootRatio(point);

    if (!limit) {
      return this.getPointFromParameter(t);
    }

    // 限制t在[0,1]范围内
    const clampedT = Math.max(0, Math.min(1, t));

    return this.getPointFromParameter(clampedT);
  }

  /**
   * 计算点在线段上是否存在垂足
   * @param point 待计算的点
   * @returns 是否存在垂足
   */
  hasFootOfPerpendicular(point: IPoint2d): boolean {
    const x = point.x,
      y = point.y;
    const x1 = this.start.x,
      y1 = this.start.y;
    const x2 = this.end.x,
      y2 = this.end.y;

    const dx = x2 - x1;
    const dy = y2 - y1;

    // 线段长度为0，垂足就是起点
    if (dx === 0 && dy === 0) return true;

    const t = ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy);

    if (t >= 0 && t <= 1) return true;

    return false;
  }

  getPointProjectOnLine(point: IPoint2d): IPoint2d {
    const line = new Line2d({ points: { p1: this._start, p2: this._end } });

    return line.pointProjectOnLine(point);
  }

  /**
   * 计算两条线段之间的有向角度，seg到this的角度
   * @param seg 另一条线段
   * @returns 有向角度（弧度）
   */
  calculateSignedAngleBetweenLine(seg: LineSeg2d): number {
    const vector1 = {
      x: seg.end.x - seg.start.x,
      y: seg.end.y - seg.start.y
    };

    const vector2 = {
      x: this.end.x - this.start.x,
      y: this.end.y - this.start.y
    };

    const magnitude1 = Math.abs(vector1.x) + Math.abs(vector1.y);
    const magnitude2 = Math.abs(vector2.x) + Math.abs(vector2.y);

    if (magnitude1 === 0 || magnitude2 === 0) {
      throw new Error('One of the line segments has zero length');
    }

    // 计算点积
    const dotProduct = vector1.x * vector2.x + vector1.y * vector2.y;

    // 计算叉积（z分量）
    const crossProduct = vector1.x * vector2.y - vector1.y * vector2.x;

    // 使用atan2计算有向角度（弧度）
    return Math.atan2(crossProduct, dotProduct);
  }

  /**
   * 检查两条线段是否共线
   * @param seg 另一条线段
   * @returns 是否共线
   */
  isCollinear(seg: LineSeg2d): boolean {
    // 处理退化情况（线段起点和终点相同）
    const isSeg1Degenerate =
      Math.abs(this.end.x - this.start.x) < thresholdValue && Math.abs(this.end.y - this.start.y) < thresholdValue;
    const isSeg2Degenerate =
      Math.abs(seg.end.x - seg.start.x) < thresholdValue && Math.abs(seg.end.y - seg.start.y) < thresholdValue;

    if (isSeg1Degenerate && isSeg2Degenerate) {
      // 两个都是点，检查是否是同一个点
      return (
        Math.abs(this.start.x - seg.start.x) < thresholdValue && Math.abs(this.start.y - seg.start.y) < thresholdValue
      );
    }

    if (isSeg1Degenerate) {
      // seg1是点，检查是否在seg2的直线上
      return seg.pointOnSegment(this.start);
    }

    if (isSeg2Degenerate) {
      // seg2是点，检查是否在seg1的直线上
      return this.pointOnSegment(seg.start);
    }

    // 常规情况：两条都是真正的线段
    // 计算方向向量
    const dx1 = this.end.x - this.start.x;
    const dy1 = this.end.y - this.start.y;
    const dx2 = seg.end.x - seg.start.x;
    const dy2 = seg.end.y - seg.start.y;

    // 计算叉积判断是否平行
    const crossProduct = dx1 * dy2 - dy1 * dx2;

    // 如果叉积不为0（超出允许的误差范围），则不平行
    if (Math.abs(crossProduct) > thresholdValue) {
      return false;
    }

    // 如果两条线段平行，检查seg2.start是否在seg1的直线上
    return (
      this.pointOnSegment(seg.start) ||
      this.pointOnSegment(seg.end) ||
      seg.pointOnSegment(this.start) ||
      seg.pointOnSegment(this.end)
    );
  }

  /**
   * 共线前提下，检查同向
   * @param seg 另一条线段
   * @returns 是否同向
   */
  isCollinearSegmentsSameDirectionRobust(seg: LineSeg2d): boolean {
    // 检查共线性
    if (!this.isCollinear(seg)) {
      throw new Error('Segments are not collinear');
    }

    // 处理退化线段（起点和终点重合）
    const isSeg1Degenerate = this.isDegenerateSegment(this);
    const isSeg2Degenerate = this.isDegenerateSegment(seg);

    // 如果两条都是退化线段，则认为同向
    if (isSeg1Degenerate && isSeg2Degenerate) {
      return true;
    }

    // 如果只有一条是退化线段，无法确定方向，默认返回false
    if (isSeg1Degenerate || isSeg2Degenerate) {
      return false;
    }

    // 计算方向向量
    const vec1 = normalizeVector(
      {
        x: this.end.x - this.start.x,
        y: this.end.y - this.start.y
      },
      thresholdValue
    );

    const vec2 = normalizeVector(
      {
        x: seg.end.x - seg.start.x,
        y: seg.end.y - seg.start.y
      },
      thresholdValue
    );

    // 比较归一化后的向量
    return Math.abs(vec1.x - vec2.x) < thresholdValue && Math.abs(vec1.y - vec2.y) < thresholdValue;
  }

  // 辅助函数：检查线段是否退化（起点和终点重合）
  isDegenerateSegment(seg: LineSeg2d): boolean {
    return Math.hypot(seg.end.x - seg.start.x, seg.end.y - seg.start.y) < thresholdValue;
  }

  /**
   * 精确计算一条线段减去多条线段后的剩余部分
   * @param mainLine 主线段
   * @param subtractLines 要减去的线段数组
   * @param epsilon 浮点数比较容差（默认1e-10）
   * @returns 剩余线段数组（保持原始坐标精确对应）
   */
  subtractSegments(subtractSegs: LineSeg2d[]): LineSeg2d[] {
    // 1. 参数校验
    if (this.isDegenerateSegment(this)) {
      return [];
    }

    // 2. 找出所有与主线段共线且重叠的有效线段
    const validSubtractLines = subtractSegs
      .filter((seg) => !this.isDegenerateSegment(seg))
      .filter((seg) => this.isCollinear(seg))
      .map((seg) => this.getOverlapSegment(seg))
      .filter((overlap) => overlap !== null);

    // 3. 如果没有有效线段要减去，返回完整主线段
    if (validSubtractLines.length === 0) {
      return [this];
    }

    // 计算各点在主线段上的参数位置 (0到1之间)
    const getLineParam = (seg: LineSeg2d): [number, number] => {
      const startParam = this.getPointParameter(seg.start);
      const endParam = this.getPointParameter(seg.end);
      return [Math.min(startParam, endParam), Math.max(startParam, endParam)];
    };

    // 主线段参数范围
    const mainParams: [number, number] = [0, 1];

    // 要减去的线段参数范围
    const subtractParams = validSubtractLines.map(getLineParam);

    // 5. 计算参数空间的差值
    const remainingParams = subtractParamIntervals(mainParams, subtractParams, thresholdValue);

    // 6. 将参数空间结果转换回实际线段
    return remainingParams.map(
      ([startParam, endParam]) =>
        new LineSeg2d(this.getPointFromParameter(startParam), this.getPointFromParameter(endParam))
    );
  }

  /**
   * 获取两条共线线段的重叠部分
   */
  getOverlapSegment(seg: LineSeg2d): LineSeg2d | null {
    // 计算otherLine两个端点在线段上的参数位置
    const param1 = this.getPointParameter(seg.start);
    const param2 = this.getPointParameter(seg.end);

    const minParam = Math.min(param1, param2);
    const maxParam = Math.max(param1, param2);

    // 计算与主线段的重叠参数范围
    const overlapStart = Math.max(0, minParam);
    const overlapEnd = Math.min(1, maxParam);

    // 如果没有有效重叠，返回null
    if (overlapStart >= overlapEnd - thresholdValue) {
      return null;
    }

    return new LineSeg2d(this.getPointFromParameter(overlapStart), this.getPointFromParameter(overlapEnd));
  }

  /**
   * 获取两条共线线段的不重叠部分
   */
  getNonOverlapSegments(seg: LineSeg2d): LineSeg2d[] {
    // 1. 找出不重叠部分
    const result: LineSeg2d[] = [];

    // 确定线段方向（水平、垂直或斜线）
    const isHorizontal = Math.abs(this.end.y - this.start.y) < thresholdValue;
    const isVertical = Math.abs(this.end.x - this.start.x) < thresholdValue;

    if (isHorizontal || isVertical) {
      const allPoints = [this.start, this.end, seg.start, seg.end];
      if (isHorizontal) {
        // 水平线段处理
        allPoints.sort((a, b) => a.x - b.x);
      } else {
        allPoints.sort((a, b) => a.y - b.y);
      }

      // 找出不连续的部分
      for (let i = 0; i < allPoints.length - 1; i++) {
        const current = allPoints[i];
        const next = allPoints[i + 1];
        const splitSeg = new LineSeg2d(current, next);
        // 如果当前点和下一个点来自不同线段，且不是同一线段的起点和终点
        const overlapSeg = this.getOverlapSegment(splitSeg);
        if (!overlapSeg) {
          result.push(splitSeg);
        } else if (overlapSeg.length < OVERLAPLENGTH) {
          result.push(splitSeg);
        }
      }
    } else {
      // 斜线线段处理
      const dx = this.end.x - this.start.x;
      const dy = this.end.y - this.start.y;

      const project = (p: IPoint2d): number => (p.x * dx + p.y * dy) / (dx * dx + dy * dy);

      const allPoints = [
        { point: this.start, pos: project(this.start) },
        { point: this.end, pos: project(this.end) },
        { point: seg.start, pos: project(seg.start) },
        { point: seg.end, pos: project(seg.end) }
      ].sort((a, b) => a.pos - b.pos);

      // 找出不连续的部分
      for (let i = 0; i < allPoints.length - 1; i++) {
        const current = allPoints[i];
        const next = allPoints[i + 1];
        const splitSeg = new LineSeg2d(current.point, next.point);
        // 如果当前点和下一个点来自不同线段，且不是同一线段的起点和终点
        const overlapSeg = this.getOverlapSegment(splitSeg);
        if (!overlapSeg) {
          result.push(splitSeg);
        } else if (overlapSeg.length < OVERLAPLENGTH) {
          result.push(splitSeg);
        }
      }
    }

    // 2. 过滤掉长度为0的线段
    return result.filter((seg) => {
      return seg.length > OVERLAPLENGTH;
    });
  }

  /**
   * 计算点在主线段上的参数位置 (0到1之间)
   */
  getPointParameter(point: IPoint2d): number {
    if (Math.abs(this.end.x - this.start.x) < thresholdValue && Math.abs(this.end.y - this.start.y) < thresholdValue) {
      return 0; // 处理退化情况
    }

    const mainVec = {
      x: this.end.x - this.start.x,
      y: this.end.y - this.start.y
    };
    const pointVec = {
      x: point.x - this.start.x,
      y: point.y - this.start.y
    };

    const dotProduct = mainVec.x * pointVec.x + mainVec.y * pointVec.y;
    const mainLengthSq = mainVec.x * mainVec.x + mainVec.y * mainVec.y;

    return Math.max(0, Math.min(1, dotProduct / mainLengthSq));
  }

  /**
   * 根据参数位置获取线段上的点
   * @param param 参数位置（0到1之间）
   * @returns 线段上的点
   */
  getPointFromParameter(param: number): IPoint2d {
    return {
      x: this.start.x + (this.end.x - this.start.x) * param,
      y: this.start.y + (this.end.y - this.start.y) * param
    };
  }

  /**
   * 根据参数位置获取线段上的点
   * @param ratio 参数位置（0到1之间）
   * @returns 线段上的点
   */
  getPointFromRatio(ratio: number): IPoint2d {
    return {
      x: this.start.x + (this.end.x - this.start.x) * ratio,
      y: this.start.y + (this.end.y - this.start.y) * ratio
    };
  }

  /**
   * 求通过点P且垂直于线段AB的直线
   * @param line 线段AB
   * @param point 点P
   * @returns 垂线的两个点（用于绘制）
   */
  getPerpendicularLine(point: IPoint2d): LineSeg2d {
    // 计算AB向量
    const abVector = {
      x: this.end.x - this.start.x,
      y: this.end.y - this.start.y
    };

    // 计算垂线向量（旋转90度）
    const perpendicularVector = {
      x: -abVector.y,
      y: abVector.x
    };

    // 标准化向量（可选）
    const length = Math.sqrt(perpendicularVector.x ** 2 + perpendicularVector.y ** 2);
    const normalized = {
      x: perpendicularVector.x / length,
      y: perpendicularVector.y / length
    };

    // 返回垂线上的两个点（距离为1单位长度）
    return new LineSeg2d(
      { x: point.x - normalized.x, y: point.y - normalized.y },
      { x: point.x + normalized.x, y: point.y + normalized.y }
    );
  }

  /**
   * 检查两条线段是否同向
   * @returns true=同向，false=不同向
   */
  isSegmentsSameDirection(seg: LineSeg2d): boolean {
    // 计算方向向量
    const subtractedPoint = Point2dUtils.subtracted(this.end, this.start);
    const v1 = new THREE.Vector2(subtractedPoint.x, subtractedPoint.y);

    const segSubtractedPoint = Point2dUtils.subtracted(seg.end, seg.start);
    const v2 = new THREE.Vector2(segSubtractedPoint.x, segSubtractedPoint.y);

    // 归一化向量
    v1.normalize();
    v2.normalize();

    // 计算点积
    const dot = v1.dot(v2);

    // 如果点积≈1，则同向（考虑浮点误差）
    return dot > 0.999;
  }

  isSegmentsAntiDirection(seg: LineSeg2d): boolean {
    // 计算方向向量
    const subtractedPoint = Point2dUtils.subtracted(this.end, this.start);
    const v1 = new THREE.Vector2(subtractedPoint.x, subtractedPoint.y);

    const segSubtractedPoint = Point2dUtils.subtracted(seg.end, seg.start);
    const v2 = new THREE.Vector2(segSubtractedPoint.x, segSubtractedPoint.y);

    // 归一化向量
    v1.normalize();
    v2.normalize();

    // 计算点积
    const dot = v1.dot(v2);

    // 如果点积≈1，则同向（考虑浮点误差）
    return dot < -0.999;
  }

  rotateAroundPoint(angle: number, center: IPoint2d = this.getSegmentCenter()): LineSeg2d {
    const cosAngle = Math.cos(angle);
    const sinAngle = Math.sin(angle);

    const rotatePoint = (point: IPoint2d): IPoint2d => {
      const translatedX = point.x - center.x;
      const translatedY = point.y - center.y;

      const rotatedX = translatedX * cosAngle - translatedY * sinAngle;
      const rotatedY = translatedX * sinAngle + translatedY * cosAngle;

      return {
        x: rotatedX + center.x,
        y: rotatedY + center.y
      };
    };
    return new LineSeg2d(rotatePoint(this.start), rotatePoint(this.end));
  }

  /**
   * 获取线段的方向单位向量
   * @returns 线段的方向单位向量
   */
  getDirectionVector(): IVector2d {
    // 计算线段的方向向量
    const dx = this.end.x - this.start.x;
    const dy = this.end.y - this.start.y;

    // 计算线段长度
    const length = Math.hypot(dx, dy);
    return { x: dx / length, y: dy / length };
  }

  /**
   * 获取线段的垂直方向单位向量
   * @returns 线段的垂直方向单位向量，指向右侧
   */
  getPerpendicularDirectionVector(): IPoint2d {
    const { x, y } = this.getDirectionVector();

    return { x: -y, y: x };
  }

  /**
   * 获取线段沿着垂线方向平移后的新线段
   * @param offset 偏移距离（正数向左偏移，负数向右偏移）
   * @returns 偏移后的线段
   */
  getOffsetLineSeg(offset: number): LineSeg2d {
    const { x: ux, y: uy } = this.getPerpendicularDirectionVector();

    // 计算偏移后的起点和终点
    const newStart: IPoint2d = {
      x: this.start.x + ux * offset,
      y: this.start.y + uy * offset
    };
    const newEnd: IPoint2d = {
      x: this.end.x + ux * offset,
      y: this.end.y + uy * offset
    };

    return new LineSeg2d(newStart, newEnd);
  }

  /**
   * 获取与 +x 轴的夹角，逆时针为正
   * @returns 夹角（弧度）
   */
  getAngleWithXAxis(): number {
    return Point2dUtils.getAngle(this.start, this.end);
  }

  isHorizontalSeg() {
    const isHorizontal = Math.abs(this._end.y - this._start.y) < thresholdValue;

    return isHorizontal;
  }

  isVerticalSeg() {
    const isVertical = Math.abs(this._end.x - this._start.x) < thresholdValue;

    return isVertical;
  }

  // 创建起点射线
  createStartRay() {
    return new Ray2d(this.start, this.getDirectionVector());
  }

  // 创建终点射线
  createEndRay() {
    const direction = this.getDirectionVector();
    return new Ray2d(this.end, { x: -direction.x, y: -direction.y });
  }

  createLine() {
    return new Line2d({ points: { p1: this._start, p2: this._end } });
  }
}

/**
 * 计算点与线段的距离，如果点的投影不在线段上，计算的是点到线段两个端点的距离中的最小值
 */
export function pointToSegmentDistance(
  pointX: number,
  pointY: number,
  startX: number,
  startY: number,
  endX: number,
  endY: number
): number {
  const A = { x: startX, y: startY };
  const B = { x: endX, y: endY };
  const P = { x: pointX, y: pointY };

  // 向量AB
  const ABx = B.x - A.x;
  const ABy = B.y - A.y;

  // 向量AP
  const APx = P.x - A.x;
  const APy = P.y - A.y;

  // 计算AB长度的平方
  const AB_squared = ABx * ABx + ABy * ABy;

  // 计算AP·AB的点积
  const AP_dot_AB = APx * ABx + APy * ABy;

  // 计算归一化参数t
  let t = AP_dot_AB / AB_squared;
  t = Math.max(0, Math.min(1, t)); // 限制在[0,1]范围内

  // 计算投影点
  const proj = {
    x: A.x + t * ABx,
    y: A.y + t * ABy
  };

  // 计算距离
  const dx = P.x - proj.x;
  const dy = P.y - proj.y;
  return Math.sqrt(dx * dx + dy * dy);
}
